<template>
  <div class="booking-wrap">

    <!-- 뒤로가기 -->
    <div class="bk-header">
      <button @click="$emit('booking-failed')" class="btn-back-top">← 검색으로 돌아가기</button>
      <div class="bk-hotel-title">{{ hotelInfo.name }}</div>
    </div>

    <!-- 단계 표시 -->
    <div class="step-bar">
      <div class="step" :class="{ active: step >= 1, done: step > 1 }">
        <span class="step-num">1</span>
        <span class="step-label">정책 확인</span>
      </div>
      <div class="step-line" :class="{ done: step > 1 }"></div>
      <div class="step" :class="{ active: step >= 2, done: step > 2 }">
        <span class="step-num">2</span>
        <span class="step-label">투숙객 입력</span>
      </div>
      <div class="step-line" :class="{ done: step > 2 }"></div>
      <div class="step" :class="{ active: step >= 3 }">
        <span class="step-num">3</span>
        <span class="step-label">예약 확정</span>
      </div>
    </div>

    <!-- ✅ 2단 레이아웃 -->
    <div class="bk-layout">
      <!-- 로딩 -->
      <div v-if="loading" class="bk-loading">
        <div class="bk-spinner"></div>
        <p>{{ loadingMsg }}</p>
      </div>

      <!-- ── STEP 1: 정책 확인 ── -->
      <div v-else-if="step === 1" class="bk-section">
        <h2 class="bk-title">예약 정책 확인</h2>

        <!-- 호텔 요약 -->
        <div class="hotel-summary">
          <div class="hs-name">{{ hotelInfo.name }}</div>
          <div class="hs-detail">
            📅 {{ searchData.check_in_date }} ~ {{ searchData.check_out_date }}
            ({{ searchData.night_count }}박)
            &nbsp;·&nbsp;
            👥 성인 {{ searchData.adult_count }}명 / {{ searchData.room_count }}실
          </div>
        </div>

        <!-- 가격 정보 -->
        <div class="price-summary">
          <div class="ps-row ps-selling">
            <span>판매가</span>
            <span class="ps-price">{{ krw(effectiveSellingKrw) }}원</span>
          </div>
          <div v-if="policyPriceChanged" class="ps-row ps-warn">
            <span>⚠️ 가격 변동 감지 (USD)</span>
            <span>
              {{ searchData.rtxPackage.room_rate }}
              →
              {{ policyData.package.room_rate }}
              {{ policyData.package.room_rate_currency }}
            </span>
          </div>
        </div>

        <!-- 취소 정책 -->
        <div v-if="policyData?.cancellation_policy" class="cancel-policy">
          <div class="cp-title">📋 취소 정책</div>
          <div v-if="policyData.cancellation_policy.remarks" class="cp-remarks">
            {{ policyData.cancellation_policy.remarks }}
          </div>
          <div v-for="(band, i) in policyData.cancellation_policy.cancellation_policies"
              :key="i" class="cp-band">
            <span class="cp-pct" :class="band.penalty_percentage === 0 ? 'pct-free' : 'pct-paid'">
              {{ band.penalty_percentage === 0 ? '무료 취소' : `위약금 ${band.penalty_percentage}%` }}
            </span>
            <span class="cp-date">
              {{ formatDate(band.date_from) }} ~ {{ formatDate(band.date_to) }}
            </span>
          </div>
        </div>

        <!-- 정책 로드 실패 시 -->
        <div v-else-if="!policyData && !loading" class="policy-warn">
          ⚠️ 예약 정책을 불러오지 못했습니다.
          <button @click="fetchBookingPolicy" class="btn-retry">다시 시도</button>
        </div>

        <button @click="goToStep2" :disabled="!bookingPolicyId" class="btn-next">
          다음 → 투숙객 입력
        </button>
      </div>


      <!-- ── STEP 2: 투숙객 입력 ── -->
      <div v-else-if="step === 2" class="bk-section">
        <h2 class="bk-title">투숙객 정보 입력</h2>

        <!-- ① 예약자 정보 -->
        <div class="guest-block block-contact">
          <div class="gb-title">👤 예약자 정보</div>
          <div class="gb-row">
            <input v-model="guestForm.contact_person.first_name" @input="guestForm.contact_person.first_name = guestForm.contact_person.first_name.toUpperCase()" placeholder="영문 이름 (First)" class="gb-input" />
            <input v-model="guestForm.contact_person.last_name"  @input="guestForm.contact_person.last_name = guestForm.contact_person.last_name.toUpperCase()" placeholder="영문 성 (Last)"    class="gb-input" />
          </div>
          <div class="gb-row">
            <input v-model="guestForm.contact_person.contact_no" placeholder="연락처 (예: 01012345678)" class="gb-input" :disabled="!!user?.email" />
            <input v-model="guestForm.contact_person.email" placeholder="이메일" class="gb-input" type="email" :disabled="!!user?.email" />
          </div>
          <div class="gb-row">
            <select v-model="guestForm.contact_person.nationality"
              class="gb-select gb-select-nation"
              @change="onContactNationalityChange">
              <option value="">국적선택 *</option>
              <option v-for="n in nationList" :key="n.nation_code" :value="n.nation_code">
                {{ n.name_kr }} ({{ n.nation_code }})
              </option>
            </select>
          </div>
        </div>

        <!-- ② 예약자 = 투숙객 여부 -->
        <div v-if="searchData.room_count === 1" class="same-guest-toggle">
          <label class="sgt-label">
            <input type="radio" v-model="sameAsContact" :value="true" @change="onSameToggle" />
            본인이 투숙객입니다 (예약자 = 대표 투숙객, 호칭과 국적은 선택필요)
          </label>
          <label class="sgt-label">
            <input type="radio" v-model="sameAsContact" :value="false" @change="onSameToggle" />
            예약자와 투숙객이 다릅니다 (대리 예약)
          </label>
        </div>

        <!-- ③ 객실별 투숙객 -->
        <div v-for="(room, ri) in guestForm.room_lead_guests" :key="ri" class="room-block">
          <div class="room-block-header">
            <span class="room-block-label">🛏 {{ ri + 1 }}호실</span>
            <span class="room-block-sub">대표 투숙객 (필수)</span>
          </div>
          <div class="gb-row">
            <select v-model="room.salutation" class="gb-select">
              <option value="">호칭</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Dr">Dr</option>
            </select>
            <input v-model="room.first_name" @input="room.first_name = room.first_name.toUpperCase()" placeholder="영문 이름 (First) *" class="gb-input" :disabled="ri === 0 && sameAsContact" />
            <input v-model="room.last_name" @input="room.last_name = room.last_name.toUpperCase()" placeholder="영문 성 (Last) *" class="gb-input" :disabled="ri === 0 && sameAsContact" />
          </div>
          <div class="gb-row">
            <select v-model="room.nationality"
              class="gb-select gb-select-nation"
              :disabled="ri === 0 && sameAsContact">
              <option value="">국적 선택 *</option>
              <option v-for="n in nationList" :key="n.nation_code" :value="n.nation_code">
                {{ n.name_kr }} ({{ n.nation_code }})
              </option>
            </select>
          </div>
        </div>

        <!-- ④ 특별 전달 사항 -->
        <div class="guest-block">
          <div class="gb-title">📝 특별 전달 사항 <span class="optional-label">(선택)</span></div>
          <textarea
            v-model="guestForm.special_requests"
            class="gb-textarea"
            placeholder="호텔에 전달할 특별 요청사항을 입력해주세요.&#10;예) 금연 객실 요청, 높은 층 선호, 조용한 객실 희망 등&#10;※ 요청사항은 보장되지 않으며, 추가비용이 발생할 수 있습니다."
            rows="4"
          />
        </div>

        <!-- ⑤ 오류 메시지 -->
        <div v-if="formErr" class="form-err">⚠️ {{ formErr }}</div>

        <!-- ⑥ 결제하기 버튼 (위젯 미표시 시) -->
        <div class="btn-row" v-if="!showPayWidget">
          <button @click="step = 1" class="btn-back">← 이전</button>
          <button @click="doPayment" class="btn-next">
            💳 결제하기 ({{ krw(effectiveSellingKrw) }}원)
          </button>
        </div>

        <!-- ⑦ 토스페이 결제위젯 -->
        <div v-show="showPayWidget" class="pay-widget-wrap">

          <div id="payment-method" class="mt-1" style="height:auto"></div>

          <!-- 결제수단 UI -->
          <!-- <div id="hotel-payment-method"></div> -->

          <!-- 약관 UI -->
          <!-- <div id="hotel-agreement"></div> -->

          <!-- 결제/취소 버튼 -->
          <div class="btn-row" style="margin-top: 16px;">
            <button @click="cancelPay" class="btn-back">취소</button>
            <button @click="doPay" class="btn-next">
              💳 결제하기 ({{ krw(effectiveSellingKrw) }}원)
            </button>            
            <!-- <button @click="doPay" class="btn-next"
              :disabled="!isAgreed"
              :style="!isAgreed ? 'opacity:0.5; cursor:not-allowed;' : ''">
              💳 결제하기 ({{ krw(searchData.selling_krw) }}원)
              <span v-if="!isAgreed" style="font-size:11px; font-weight:400;">
                (약관 동의 필요)
              </span>
            </button> -->
          </div>

        </div>

      </div>


      <!-- ── STEP 3: 예약 확정 대기 ── -->
      <div v-else-if="step === 3" class="bk-section">
        <h2 class="bk-title">예약 처리 중</h2>

        <div class="status-box" :class="statusClass">
          <div class="status-icon">{{ statusIcon }}</div>
          <div class="status-text">{{ statusText }}</div>
          <div v-if="bookingId" class="status-id">예약 ID: {{ bookingId }}</div>
          <div v-if="bookStatus === 'bkg-ps'" class="status-sub">
            공급사 확인 중입니다. 수 분에서 수 시간 소요될 수 있습니다.
          </div>
        </div> 

        <!-- 확정 완료 -->
        <div v-if="bookStatus === 'bkg-cf'" class="result-box result-success">
          <p>🎉 예약이 확정되었습니다!</p>
          <div class="result-info">
            <div class="ri-row">
              <span>예약번호</span>
              <strong>{{ bookingNo }}</strong>
            </div>
            <div class="ri-row" style="font-size:11px; color:#999;">
              <span>RTX ID</span>
              <span>{{ bookingId }}</span>
            </div>
            <div class="ri-row"><span>호텔</span><strong>{{ hotelInfo.name }}</strong></div>
            <div class="ri-row"><span>체크인</span><strong>{{ searchData.check_in_date }}</strong></div>
            <div class="ri-row"><span>체크아웃</span><strong>{{ searchData.check_out_date }}</strong></div>
          </div>
          <button @click="doCancel" class="btn-cancel">예약 취소</button>
          <button @click="$emit('booking-complete', bookingId)" class="btn-next" style="margin-top:8px;">
            완료 →
          </button>
        </div>

        <!-- 실패 -->
        <div v-if="bookStatus === 'bkg-af'" class="result-box result-fail">
          <p>❌ 예약에 실패했습니다.</p>
          <p class="fail-desc">다른 객실을 선택하거나 다시 검색해주세요.</p>
          <button @click="$emit('booking-failed')" class="btn-back">← 다시 검색</button>
        </div>

        <!-- 취소 완료 -->
        <div v-if="bookStatus === 'bkg-cx'" class="result-box result-cancel">
          <p>🚫 예약이 취소되었습니다.</p>
          <button @click="$emit('booking-failed')" class="btn-back">← 검색으로 돌아가기</button>
        </div>

      </div>

      <!-- 우: 예약 요약 패널 -->
      <aside class="bk-summary">
        <!-- 호텔 이미지 -->
        <div class="bs-img-wrap">
          <img v-if="hotelInfo.heroImg"
            :src="`${rakutenImageHost}/${hotelInfo.hotel_id}/${hotelInfo.heroImg}`"
            class="bs-img" :alt="hotelInfo.name"
            @error="e => e.target.style.display='none'" />
          <div v-else class="bs-img-placeholder">🏨</div>
        </div>

        <!-- 호텔명 -->
        <div class="bs-hotel-name">{{ hotelInfo.name }}</div>
        <div class="bs-hotel-sub">{{ hotelInfo.original_name }}</div>

        <div class="bs-divider"></div>

        <!-- 체크인/아웃 -->
        <div class="bs-row">
          <div class="bs-col">
            <div class="bs-label">체크인</div>
            <div class="bs-val">{{ searchData.check_in_date }}</div>
          </div>
          <div class="bs-col-sep">→</div>
          <div class="bs-col">
            <div class="bs-label">체크아웃</div>
            <div class="bs-val">{{ searchData.check_out_date }}</div>
          </div>
        </div>

        <!-- 숙박/인원/객실 -->
        <div class="bs-tags">
          <span class="bs-tag">🌙 {{ searchData.night_count }}박</span>
          <span class="bs-tag">👥 성인 {{ searchData.adult_count }}명</span>
          <span class="bs-tag">🛏 {{ searchData.room_count }}실</span>
        </div>

        <div class="bs-divider"></div>

        <!-- 객실 정보 -->
        <div v-if="searchData.room_desc || searchData.selling_krw" class="bs-room-info">
          <div class="bs-label">선택 객실</div>
          <div v-if="searchData.room_desc" class="bs-room-name">{{ searchData.room_desc }}</div>
          <div v-if="searchData.supplier_desc" class="bs-room-sub">{{ searchData.supplier_desc }}</div>
          <!-- ✅ 식사 정보 -->
          <div v-if="mealText" class="bs-meal-tag">🍽 {{ mealText }}</div>
        </div>

        <div class="bs-divider"></div>

        <!-- 취소 정책 요약 -->
        <div v-if="searchData.cancel_policy" class="bs-cancel">
          <div class="bs-label">취소 정책</div>
          <div class="bs-cancel-val">{{ cancelSummaryText }}</div>
        </div>

        <div class="bs-divider"></div>

        <!-- 가격 -->
        <div class="bs-price-wrap">
          <div class="bs-price-label">결제 금액</div>
          <!-- 우측패널금액 -->
          <div class="bs-price-val">{{ krw(effectiveSellingKrw) }}<small>원</small></div>
          <div v-if="searchData.night_count > 0" class="bs-price-sub">
            1박 기준 약 {{ krw(Math.round(searchData.selling_krw / searchData.night_count)) }}원
          </div>
        </div>

        <!-- ✅ 추가 안내 -->
        <div v-if="addInfoList.length > 0" class="bs-addinfo">
          <div class="bs-addinfo-title fw-bold">⚠️ 추가안내사항</div>
          <div v-for="(info, i) in addInfoList" :key="i" class="bs-addinfo-item">
            <!-- <div class="bs-addinfo-name">{{ info.addTitle }}</div> -->
            <div class="bs-addinfo-content">{{ info.addContent }}</div>
          </div>
        </div>

      </aside>

    </div>  <!-- bk-layout -->
  </div>
</template>


<script>
import { loadPaymentWidget, ANONYMOUS } from "@tosspayments/payment-widget-sdk";

export default {
  name: 'RtxBookingB2C',

  props: {
    hotelInfo:  { type: Object, required: true },
    searchData: { type: Object, required: true }
  },

  emits: ['booking-complete', 'booking-failed'],

  // ✅ RtxRoomPlanB2C와 동일한 식사코드 매핑
  FOOD: {
    1:'룸온리', 2:'조식포함', 3:'점심포함',
    4:'저녁포함', 5:'하프보드', 6:'풀보드', 7:'올인클루시브'
  },

  data() {
    return {
      step:       1,
      loading:    false,
      loadingMsg: '',

      policyData:       null,
      bookingPolicyId:  null,
      bookingId:        null,
      bookingNo:        null,
      bookStatus:       null,

      sameAsContact: false,   // 예약자 = 대표투숙객 여부

      guestForm: {
        room_lead_guests: [],
        contact_person: {
          first_name:  '',
          last_name:   '',
          contact_no:  '',
          email:       '',
          nationality: 'KR',
        },
        special_requests: ''
      },

      formErr:   '',
      pollTimer: null,
      pollCount: 0,
      pollPhase: 1,

      addInfoList: [],              // 추가안내 목록
      nationList: [],               // 국적리스트

      booking_no: null,            // NTB예번 & Toss OrderID로 사용

      // 토스PG결제용 정보
      showPayWidget: false,        // 결제위젯 표시 여부
      paymentWidget: null,
      paymentMethodWidget: null,
      // clientKey: "test_ck_YyZqmkKeP8gG2Klanln8bQRxB9lG",
      clientKey: "live_ck_jZ61JOxRQVEZXa1aoR0rW0X9bAqw",
      customerKey: "Ntabi-1936=@",

      // 가격 변동 시 조정된 가격 (로컬 관리)
      adjustedRoomRate: null,
      adjustedSellingKrw: null,
    };
  },

  computed: {
    user() { return this.$store.state.user; },

    FOOD() { return this.$options.FOOD; },

    // ✅ 식사 정보 텍스트 (searchData.rtxPackage.room_details.food 기반)
    mealText() {
      const foodCode = this.searchData?.rtxPackage?.room_details?.food;
      if (foodCode == null) return '';
      return this.FOOD[foodCode] || '';
    },

    policyPriceChanged() {
      const oldRate = this.searchData?.rtxPackage?.room_rate;
      const newRate = this.policyData?.package?.room_rate;
      if (oldRate == null || newRate == null) return false;
      return oldRate !== newRate;
    },

    statusClass() {
      if (this.bookStatus === 'bkg-cf') return 'status-success';
      if (this.bookStatus === 'bkg-af') return 'status-fail';
      if (this.bookStatus === 'bkg-cx') return 'status-cancel';
      return 'status-pending';
    },

    statusIcon() {
      if (this.bookStatus === 'bkg-cf') return '✅';
      if (this.bookStatus === 'bkg-af') return '❌';
      if (this.bookStatus === 'bkg-cx') return '🚫';
      return '⏳';
    },

    statusText() {
      const map = {
        'bkg-ns': '예약 준비 중...',
        'bkg-ip': '예약 처리 중...',
        'bkg-ps': '공급사 확인 대기 중...',
        'bkg-cf': '예약 확정!',
        'bkg-af': '예약 실패',
        'bkg-cx': '예약 취소됨'
      };
      return map[this.bookStatus] || '처리 중...';
    },

    rakutenImageHost() { return this.$store.state.rakutenImageHost || ''; },

    cancelSummaryText() {
      const policy = this.searchData?.cancel_policy;
      if (!policy?.cancellation_policies?.length) return '정책 없음';
      const paid = policy.cancellation_policies.find(p => p.penalty_percentage === 100);
      if (paid) {
        const d = new Date(paid.date_from);
        return `${d.getMonth() + 1}/${d.getDate()} 이전 무료취소`;
      }
      return policy.remarks || '취소정책 확인 필요';
    },

    // 실제 사용할 판매가 (조정됐으면 조정값, 아니면 원래값)
    effectiveSellingKrw() {
      return this.adjustedSellingKrw || this.searchData.selling_krw;
    },
  },

  async created() {
    const roomCount = this.searchData.room_count || 1;

    // 객실 수만큼 투숙객 폼 초기화 (추가투숙객 배열 포함)
    this.guestForm.room_lead_guests = Array.from({ length: roomCount }, () => ({
      salutation:        '',
      first_name:        '',
      last_name:         '',
      nationality:       '',
    }));

  // ✅ 회원인 경우 contact_person 자동입력
  if (this.user?.email) 
  {
    this.guestForm.contact_person.email      = this.user.email      || '';
    this.guestForm.contact_person.contact_no = this.user.phone      || this.user.alimCallC || '';
  }

    await this.fetchBookingPolicy();
    await this.fetchAddInfo();          //추가안내사항
    await this.fetchNationList();       //국적리스트
  },

  beforeUnmount() {
    this.stopPoll();
  },

  methods: {

    // ── 예약자 = 투숙객 토글 ─────────────────────────
    onContactNationalityChange() {
      if (this.sameAsContact) 
      {
        const lead = this.guestForm.room_lead_guests[0];
        if (lead) lead.nationality = this.guestForm.contact_person.nationality;
      }
    },
    // ✅ 동일인 토글 시 이름 + 국적 함께 복사
    onSameToggle() {
      const lead = this.guestForm.room_lead_guests[0];
      if (this.sameAsContact) 
      {
        const c = this.guestForm.contact_person;
        lead.first_name  = c.first_name;
        lead.last_name   = c.last_name;
        lead.nationality = c.nationality || 'KR';  // ✅ 국적도 함께
      } 
      else 
      {
        lead.first_name  = '';
        lead.last_name   = '';
        lead.nationality = 'KR';
      }
    },

    // 현지결제관련 추가안내
    async fetchAddInfo() {
      try 
      {
        const result = await this.$api('/api/rakutenAddInfoByDate', {
          param: [this.searchData.check_in_date, this.searchData.check_in_date]   // 체크인일 기준
        });
        this.addInfoList = Array.isArray(result) ? result : [];
      } 
      catch (e) 
      {
        console.error('[AddInfo] 추가안내 조회 오류:', e);
        this.addInfoList = [];
      }
    },

    // 국적리스트
    async fetchNationList() {
      try 
      {
        const result = await this.$api('/api/rtxNationList', {});
        this.nationList = Array.isArray(result) ? result : [];
      } 
      catch (e) 
      {
        console.error('[NationList] 국적리스트업 오류:', e);
        this.nationList = [];
      }
    },


    // ① PostBookingPolicy
    async fetchBookingPolicy() {
      this.loading    = true;
      this.loadingMsg = '예약 정책을 불러오는 중...';
      try {
        const result = await this.$api('/api/rtx/postBookingPolicy', {
          param: [{
            session_id: this.searchData.session_id,
            search:     this.searchData.rtxSearch,
            package:    this.searchData.rtxPackage
          }]
        });

        if (result.success) {
          this.policyData      = result.data;
          this.bookingPolicyId = result.booking_policy_id;
        } else {
          throw new Error(result.message || '정책 조회 실패');
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || '정책 조회 중 오류가 발생했습니다.';
        console.error('[RTX] BookingPolicy 오류:', e);
        this.$swal({ icon: 'error', title: '정책 조회 실패', text: msg });
      } finally {
        this.loading = false;
      }
    },

    // STEP 1 → 2
    goToStep2() {
      if (!this.bookingPolicyId) 
      {
        this.$swal({ icon: 'warning', text: '예약 정책을 먼저 불러와주세요.' });
        return;
      }

      if (this.policyPriceChanged) 
      {
        const oldRate = this.searchData.rtxPackage.room_rate;
        const newRate = this.policyData.package.room_rate;
        const newSellingKrw = Math.round(
          (newRate / oldRate) * this.searchData.selling_krw
        );

        this.$swal({
          icon:  'warning',
          title: '요금이 변동되었습니다',
          html:  `호텔 측에서 요금이 변경되었습니다.<br><br>
                  <b>변경 전:</b> ${oldRate} USD (${this.krw(this.searchData.selling_krw)}원)<br>
                  <b>변경 후:</b> ${newRate} USD (${this.krw(newSellingKrw)}원)<br><br>
                  변경된 요금으로 예약을 진행하시겠습니까?`,
          showCancelButton:   true,
          confirmButtonText:  '변경된 요금으로 진행',
          cancelButtonText:   '다시 검색',
          confirmButtonColor: '#1a6eb5',
          cancelButtonColor:  '#aaa',
        }).then((result) => {
          if (result.isConfirmed) {
            // ✅ props 수정 대신 로컬 data에 저장
            this.adjustedRoomRate   = newRate;
            this.adjustedSellingKrw = newSellingKrw;
            this.step = 2;
          } else {
            this.$emit('booking-failed');
          }
        });
        return;
      }

      this.step = 2;
    },


    /***************************************************************************************
     * STEP 2 → 결제창(Toss PG V1)
    ***************************************************************************************/
    // ── 위젯 제거 ─────────────────────────
    destroyWidgets() {
      try 
      {
        if (this.paymentMethodWidget) 
        {
          this.paymentMethodWidget.destroy();
          this.paymentMethodWidget = null;
        }
        if (this.agreementWidget) 
        {
          this.agreementWidget.destroy();
          this.agreementWidget = null;
        }
      } 
      catch (e) 
      {
        // destroy 중 오류는 무시
      }
    },

    // ── 결제창 호출 ─────────────────────────
    async doPayment() {
      this.formErr = this.validateGuests();
      if (this.formErr) return;

      // NTB예번 및 orderID생성
      const now = new Date();
      const yy  = String(now.getFullYear()).slice(2);
      const mm  = String(now.getMonth() + 1).padStart(2, '0');
      const dd  = String(now.getDate()).padStart(2, '0');
      const hh  = String(now.getHours()).padStart(2, '0');
      const mi  = String(now.getMinutes()).padStart(2, '0');
      const ss  = String(now.getSeconds()).padStart(2, '0');
      const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
      this.booking_no = `NH-${yy}${mm}${dd}${hh}${mi}${ss}-${rand}`;

      // ✅ sessionStorage에 예약 데이터 저장
      const pm = this.searchData;
      // 조정된 가격우선적용
      const sellingKrw = this.adjustedSellingKrw || pm.selling_krw;

      sessionStorage.setItem('hotelBookingData', JSON.stringify({
        booking_no:        this.booking_no,
        booking_policy_id: this.bookingPolicyId,
        session_id:        pm.session_id,
        room_lead_guests:  this.guestForm.room_lead_guests.map(r => ({
          salutation:  r.salutation,
          first_name:  r.first_name,
          last_name:   r.last_name,
          nationality: r.nationality,
          remarks:     this.guestForm.special_requests || ''
        })),
        contact_person:  this.guestForm.contact_person,
        hotel_id:        this.hotelInfo.hotel_id,
        hotel_name:      this.hotelInfo.name,
        check_in:        pm.check_in_date,
        check_out:       pm.check_out_date,
        night_count:     pm.night_count,
        room_count:      pm.room_count,
        adult_count:     pm.adult_count,
        children:        pm.children || [],
        room_desc:       pm.room_desc     || '',
        supplier_desc:   pm.supplier_desc || '',
        cost_krw:        pm.cost_krw,
        selling_krw:     sellingKrw,   // ← 조정된 가격
        non_refundable:  pm.rtxPackage?.non_refundable ?? null,
        non_smoking:     pm.rtxPackage?.room_details?.non_smoking ?? null,
        food_code:       pm.rtxPackage?.room_details?.food ?? null,
        rate_plan_code:  pm.rtxPackage?.room_details?.rate_plan_code || '',
        room_code:       pm.rtxPackage?.room_details?.room_code      || '',
        rate_type:       pm.rtxPackage?.rate_type || '',
        tax_usd:         pm.rtxPackage?.taxes_and_fees?.tax?.value        ?? 0,
        resort_fee_usd:  pm.rtxPackage?.hotel_fees?.resort_fee?.value     ?? 0,
        cancel_policy:   pm.cancel_policy  || null,
        special_request: this.guestForm.special_requests || '',
        contact_name:    `${this.guestForm.contact_person.first_name} ${this.guestForm.contact_person.last_name}`,
        contact_no:      this.guestForm.contact_person.contact_no,
        contact_email:   this.guestForm.contact_person.email || null, 
        mem_flag:        pm.memFlag  || 'N',              // 회원 여부 (Y/N)
        mem_id:          pm.memId    || null,
        mem_ship:        pm.memShip  ?? null,             // 유료멤버십가입여부
        mem_level:       pm.memLevel ?? null,             // 무료(0) / 유료(1,2,3...)
        hotel_tel:       pm.hotel_tel || null,            // 호텔연락처
      }));

      try 
      {
        this.loading    = true;
        this.loadingMsg = '결제창 호출...';

        // ------  결제위젯 초기화 ------
        this.paymentWidget = await loadPaymentWidget(this.clientKey, ANONYMOUS);

        // loading 해제 + 위젯표시
        this.loading       = false;
        this.showPayWidget = true;

        // ✅ DOM 업데이트 후 렌더링
        await this.$nextTick();

        // ------  결제 UI 렌더링 ------
        this.paymentWidget.renderPaymentMethods('#payment-method', 
          { value: sellingKrw },    // ← 조정된 가격
          { variantKey: 'DEFAULT' }
        );
      } 
      catch (e) 
      {
        console.error('[Toss PG] 결제창 초기화 오류:', e);
        sessionStorage.removeItem('hotelBookingData');
        this.$swal({ icon: 'error', title: '결제창 오류', text: e.message });
      } 
    },

    async doPay() {
      try
      {
        if(this.paymentWidget)
        {
          await this.paymentWidget.requestPayment({
            orderId:    this.booking_no,
            orderName:  this.hotelInfo.name,
            successUrl: 'https://ntabi.co.kr/rtxSuccess_pg',
            failUrl:    'https://ntabi.co.kr/rtxFail_pg',
            // successUrl: 'http://localhost/rtxSuccess_pg',
            // failUrl:    'http://localhost/rtxFail_pg',
            customerEmail:  this.guestForm.contact_person.email || '',
            customerName:   `${this.guestForm.contact_person.first_name} ${this.guestForm.contact_person.last_name}`,
            taxFreeAmount:  this.searchData.selling_krw
          });
        }        
      }
      catch(e)
      {
        console.error('[Toss PG] 결제진행오류:', e);
        this.$swal({ icon: 'warning', html: '결제가 완료되지 안았습니다', text: e.message });
      }
    },

    // ── 결제 취소 ─────────────────────────
    cancelPay() {
      this.destroyWidgets();    // ✅ 위젯 제거
      this.paymentWidget  = null;
      this.showPayWidget  = false;
      this.booking_no     = null;
      sessionStorage.removeItem('hotelBookingData');
    },

    // ② PostPreBook: 보안을 위해 백엔드으로 처리하고 프론트는 결과 받아서 PostBook 호출만 담당으로 변경(2026-05-29)
    async doPreBook() {
      this.formErr = this.validateGuests();
      if (this.formErr) return;

      this.loading    = true;
      this.loadingMsg = '예약정보를 등록하는 중...';
      
      try 
      {
        const roomGuests = this.guestForm.room_lead_guests.map(r => ({
          salutation:  r.salutation,
          first_name:  r.first_name,
          last_name:   r.last_name,
          nationality: r.nationality,
          remarks:     this.guestForm.special_requests || ''
        }));

        const pm = this.searchData;

        // ✅ 백엔드 하나로 모든 처리 위임
        const result = await this.$api('/api/rtx/postPreBook', {
          param: [{
            // RTX 호출용
            booking_policy_id: this.bookingPolicyId,
            session_id:        pm.session_id,
            room_lead_guests:  roomGuests,
            contact_person:    this.guestForm.contact_person,

            // 호텔 정보
            hotel_id:    this.hotelInfo.hotel_id,
            hotel_name:  this.hotelInfo.name,

            // 투숙 조건
            check_in:    pm.check_in_date,
            check_out:   pm.check_out_date,
            night_count: pm.night_count,
            room_count:  pm.room_count,
            adult_count: pm.adult_count,
            children:    pm.children || [],

            // 객실 정보
            room_desc:      pm.room_desc     || '',
            supplier_desc:  pm.supplier_desc || '',

            // 가격 (백엔드에서 margin 계산)
            cost_krw:    pm.cost_krw,
            selling_krw: pm.selling_krw,

            // 객실 상세
            non_refundable: pm.rtxPackage?.non_refundable ?? null,
            non_smoking:    pm.rtxPackage?.room_details?.non_smoking ?? null,
            food_code:      pm.rtxPackage?.room_details?.food ?? null,
            rate_plan_code: pm.rtxPackage?.room_details?.rate_plan_code || '',
            room_code:      pm.rtxPackage?.room_details?.room_code      || '',
            rate_type:      pm.rtxPackage?.rate_type || '',

            // 세금/수수료
            tax_usd:        pm.rtxPackage?.taxes_and_fees?.tax?.value         ?? 0,
            resort_fee_usd: pm.rtxPackage?.hotel_fees?.resort_fee?.value      ?? 0,

            // 취소정책 / 특별요청
            cancel_policy:   pm.cancel_policy || null,
            special_request: this.guestForm.special_requests || '',

            // 예약자 정보
            contact_name:  `${this.guestForm.contact_person.first_name} ${this.guestForm.contact_person.last_name}`,
            contact_no:    this.guestForm.contact_person.contact_no,
            contact_email: this.guestForm.contact_person.email || null,
 
            // 회원 정보
            mem_flag:  pm.memFlag  || 'N',
            mem_id:    pm.memId    || null,
            mem_level: pm.memLevel ?? null,
            mem_ship:  pm.memShip  ?? null,       // 유료멤버십가입여부
            hotel_tel:   pm.hotel_tel || null,    // 호텔연락처
          }]
        });

        if (!result.success) throw new Error(result.message || 'PreBook 실패');

        this.bookingId = result.booking_id;
        this.bookingNo = result.booking_no;

        // ✅ PostBook 호출
        await this.doBook();

      } 
      catch (e) 
      {
        console.error('[RTX] PreBook 오류:', e);
        this.$swal({ icon: 'error', title: '예약선처리(PreBook) 실패', text: e.message });
      } 
      finally 
      {
        this.loading = false;
      }
    },

    // ③ PostBook
    async doBook() {
      this.loading    = true;
      this.loadingMsg = '예약을 생성하는 중...';
      this.step       = 3;
      try {
        const result = await this.$api('/api/rtx/postBook', {
          param: [{ booking_id: this.bookingId, session_id: this.searchData.session_id }]
        });
        if (result.success) {
          this.bookStatus = result.status;
          this.startPoll();
        } else {
          throw new Error(result.message || 'Book 실패');
        }
      } catch (e) {
        console.error('[RTX] Book 오류:', e);
        this.bookStatus = 'bkg-af';
      } finally {
        this.loading = false;
      }
    },

    // ④ GetBookStatus 폴링
    startPoll() {
      this.pollCount = 0;
      this.pollPhase = 1;

      const poll = async () => {
        if (!this.bookingId) return;
        this.pollCount++;

        try {
          const result = await this.$api('/api/rtx/getBookStatus', {
            param: [{ booking_id: this.bookingId, session_id: this.searchData.session_id }]
          });
          if (result.success) this.bookStatus = result.status;
        } catch (e) {
          console.error('[RTX] 폴링 오류:', e);
        }

        if (['bkg-cf', 'bkg-af', 'bkg-cx'].includes(this.bookStatus)) {
          this.stopPoll();
          return;
        }

        let nextInterval = 5000;
        if (this.pollCount > 12)     nextInterval = 60000;
        if (this.pollCount > 12 + 5) nextInterval = 300000;
        this.pollTimer = setTimeout(poll, nextInterval);
      };

      this.pollTimer = setTimeout(poll, 5000);
    },

    stopPoll() {
      if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
    },

    // ⑤ PostCancel
    async doCancel() {
      const confirm = await this.$swal.fire({
        icon: 'warning', title: '예약을 취소하시겠습니까?',
        text: '취소 후 복구가 불가능합니다.',
        showCancelButton: true, confirmButtonText: '취소 진행', cancelButtonText: '닫기'
      });
      if (!confirm.isConfirmed) return;

      this.loading    = true;
      this.loadingMsg = '예약을 취소하는 중...';
      try {
        const result = await this.$api('/api/rtx/postCancel', {
          param: [{ booking_id: this.bookingId, session_id: this.searchData.session_id }]
        });
        if (result.success) { this.bookStatus = result.status; this.startPoll(); }
        else throw new Error(result.message || '취소 실패');
      } catch (e) {
        console.error('[RTX] 취소 오류:', e);
        this.$swal({ icon: 'error', title: '취소 실패', text: e.message });
      } finally {
        this.loading = false;
      }
    },

    // ── 유효성 검사 ──────────────────────────────────
    validateGuests() {
      // 예약자 정보
      const c = this.guestForm.contact_person;
      if (!c.first_name)    return '예약자 이름(First)을 입력해주세요.';
      if (!c.last_name)     return '예약자 성(Last)을 입력해주세요.';
      if (!c.contact_no)    return '예약자 연락처를 입력해주세요.';
      if (!c.email) return '예약자 이메일을 입력해주세요.';

      // 객실별 대표 투숙객
      for (let i = 0; i < this.guestForm.room_lead_guests.length; i++) 
      {
        const g = this.guestForm.room_lead_guests[i];
        if (!g.first_name)  return `${i + 1}호실 대표 투숙객 이름(First)을 입력해주세요.`;
        if (!g.last_name)   return `${i + 1}호실 대표 투숙객 성(Last)을 입력해주세요.`;
        if (!g.nationality) return `${i + 1}호실 국적 코드를 입력해주세요. (예: KR)`;
      }
      return '';
    },

    // ── 유틸 ─────────────────────────────────────────
    krw(val) {
      if (val == null) return '-';
      return new Intl.NumberFormat('ko-KR').format(val);
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
    }
  }
};
</script>


<style scoped>
.booking-wrap {
  max-width: 720px; margin: 0 auto; padding: 24px 16px;
  font-family: 'Noto Sans KR', sans-serif;
}

/* 헤더 */
.bk-header { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
.btn-back-top {
  background: none; border: 1px solid #ddd; border-radius: 8px;
  padding: 7px 14px; font-size: 13px; color: #555; cursor: pointer;
  white-space: nowrap; transition: all 0.15s;
}
.btn-back-top:hover { border-color: #1a6eb5; color: #1a6eb5; }
.bk-hotel-title { font-size: 16px; font-weight: 800; color: #1a1a2e; }

/* 단계 바 */
.step-bar { display: flex; align-items: center; justify-content: center; margin-bottom: 32px; }
.step { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.step-num {
  width: 32px; height: 32px; border-radius: 50%;
  background: #e2e8f0; color: #999;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; transition: all 0.2s;
}
.step.active .step-num { background: #1a6eb5; color: #fff; }
.step.done   .step-num { background: #86cd53; color: #fff; }
.step-label { font-size: 11px; color: #999; white-space: nowrap; }
.step.active .step-label { color: #1a6eb5; font-weight: 700; }
.step-line { flex: 1; height: 2px; background: #e2e8f0; min-width: 48px; transition: background 0.2s; }
.step-line.done { background: #86cd53; }

/* 로딩 */
.bk-loading { text-align: center; padding: 60px 0; }
.bk-spinner {
  width: 44px; height: 44px; margin: 0 auto 16px;
  border: 4px solid #eee; border-top-color: #1a6eb5;
  border-radius: 50%; animation: spin 0.8s linear infinite;
}
.bk-loading p { color: #888; font-size: 14px; }

/* 섹션 */
.bk-section {
  flex: 1;              /* 550px 고정값 대신 */
  min-width: 0;         /* flex item이 내용 때문에 안 줄어들게 막는 기본 버그 방지 */
  background: #fff; border-radius: 14px; padding: 28px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}
.bk-title { font-size: 18px; font-weight: 800; color: #1a1a2e; margin-bottom: 20px; }

/* 호텔 요약 */
.hotel-summary {
  background: #f8f9ff; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;
}
.hs-name   { font-size: 15px; font-weight: 800; color: #1a1a2e; margin-bottom: 5px; }
.hs-detail { font-size: 13px; color: #666; line-height: 1.6; }

/* 가격 요약 */
.price-summary {
  border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;
}
.ps-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 14px; color: #555; }
.ps-selling .ps-price { font-size: 22px; font-weight: 900; color: #1a1a2e; }
.ps-warn { color: #e53e3e; font-weight: 700; font-size: 13px; }

/* 취소 정책 */
.cancel-policy { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; margin-bottom: 20px; }
.cp-title   { font-size: 13px; font-weight: 700; color: #444; margin-bottom: 8px; }
.cp-remarks { font-size: 12px; color: #666; margin-bottom: 10px; line-height: 1.6; }
.cp-band    { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }
.cp-pct     { font-size: 13px; font-weight: 700; }
.pct-free   { color: #276749; }
.pct-paid   { color: #c53030; }
.cp-date    { font-size: 12px; color: #888; }

/* 정책 로드 실패 */
.policy-warn { text-align: center; color: #e53e3e; font-size: 14px; padding: 20px 0; }
.btn-retry   { margin-left: 12px; padding: 6px 14px; background: #f0f0f0; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; }

/* 예약자 = 투숙객 토글 */
.same-guest-toggle {
  display: flex; flex-direction: column; gap: 8px;
  background: #f8f9ff; border-radius: 10px;
  padding: 14px 16px; margin-bottom: 14px;
  border: 1px solid #dce6ff;
}
.sgt-label {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; color: #444; cursor: pointer;
}
.sgt-label input[type="radio"] { accent-color: #1a6eb5; width: 16px; height: 16px; cursor: pointer; }

/* 투숙객 공통 블록 */
.guest-block {
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 16px; margin-bottom: 14px;
}
.block-contact { border-color: #dce6ff; background: #fafbff; }

.gb-title {
  font-size: 13px; font-weight: 700; color: #444; margin-bottom: 10px;
  display: flex; align-items: center; gap: 6px;
}
.optional-label { font-size: 11px; font-weight: 400; color: #aaa; }
.gb-row   { display: flex; gap: 8px; margin-bottom: 8px; }
.gb-input {
  flex: 1; padding: 9px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 14px; outline: none; transition: border-color 0.15s;
}
.gb-input:focus  { border-color: #1a6eb5; }
.gb-input:disabled { background: #f5f5f5; color: #aaa; cursor: not-allowed; }
.gb-select {
  padding: 9px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 14px; width: 90px; outline: none;
}
.gb-hint { font-size: 12px; color: #999; margin-top: 4px; }

/* 텍스트에리어 */
.gb-textarea {
  width: 100%; padding: 10px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 13px; outline: none;
  resize: vertical; line-height: 1.6; box-sizing: border-box;
  font-family: 'Noto Sans KR', sans-serif; color: #444;
  transition: border-color 0.15s;
}
.gb-textarea:focus { border-color: #1a6eb5; }

/* 객실 블록 */
.room-block {
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 16px; margin-bottom: 14px;
}
.room-block-header {
  display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
}
.room-block-label {
  font-size: 14px; font-weight: 800; color: #1a6eb5;
}
.room-block-sub {
  font-size: 12px; color: #888; font-weight: 500;
}

.form-err {
  color: #e53e3e; font-size: 13px; margin-bottom: 12px;
  padding: 8px 12px; background: #fff5f5; border-radius: 8px;
}

/* 버튼 */
.btn-row { display: flex; gap: 10px; margin-top: 20px; }
.btn-next {
  flex: 1; padding: 13px; background: #1a6eb5; color: #fff;
  border: none; border-radius: 8px;
  font-size: 15px; font-weight: 700; cursor: pointer; transition: background 0.2s;
}
.btn-next:hover:not(:disabled)  { background: #86cd53; }
.btn-next:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-back {
  padding: 13px 20px; background: #f0f0f0; color: #555;
  border: none; border-radius: 8px;
  font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.btn-back:hover { background: #e2e8f0; }

/* 상태 박스 */
.status-box { text-align: center; padding: 36px 20px; border-radius: 14px; margin-bottom: 20px; }
.status-pending { background: #fffbeb; border: 1px solid #fde68a; }
.status-success { background: #f0fff4; border: 1px solid #c6f6d5; }
.status-fail    { background: #fff5f5; border: 1px solid #fed7d7; }
.status-cancel  { background: #f7f7f7; border: 1px solid #e2e8f0; }
.status-icon    { font-size: 48px; margin-bottom: 12px; }
.status-text    { font-size: 18px; font-weight: 800; color: #1a1a2e; }
.status-id      { font-size: 12px; color: #888; margin-top: 6px; }
.status-sub     { font-size: 12px; color: #888; margin-top: 8px; line-height: 1.6; }

/* 결과 박스 */
.result-box { border-radius: 12px; padding: 24px; margin-top: 16px; text-align: center; }
.result-success { background: #f0fff4; }
.result-fail    { background: #fff5f5; }
.result-cancel  { background: #f7f7f7; }
.result-box p   { font-size: 16px; font-weight: 700; margin-bottom: 16px; }
.fail-desc      { font-size: 13px; color: #888; margin-top: -10px; }

.result-info {
  background: #fff; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; text-align: left;
}
.ri-row {
  display: flex; justify-content: space-between; padding: 5px 0;
  font-size: 13px; color: #555; border-bottom: 1px solid #f0f0f0;
}
.ri-row:last-child { border-bottom: none; }
.ri-row strong { color: #1a1a2e; }

.btn-cancel {
  width: 100%; padding: 11px; background: #fff5f5; color: #c53030;
  border: 1px solid #fed7d7; border-radius: 8px;
  font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; margin-bottom: 8px;
}
.btn-cancel:hover { background: #fed7d7; }

@keyframes spin { to { transform: rotate(360deg); } }

/* ✅ 2단 레이아웃 */
.bk-layout {
  display: flex;
  gap: 24px;
  align-items: stretch;       /*동일높이유지*/
}
.bk-main {
  flex: 1;
  min-width: 0;
}

/* ✅ 우측 요약 패널 */
.bk-summary {
  width: 300px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e8edf2;
  border-radius: 14px;
  overflow: hidden;
  position: sticky;
  top: 80px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  align-self: flex-start;  /* ✅ sticky가 stretch와 충돌하지 않도록 */
  min-height: 100%;        /* ✅ 높이 맞춤 */
}

.bs-img-wrap { width: 100%; height: 180px; overflow: hidden; background: #f0f4f8; }
.bs-img      { width: 100%; height: 180px; object-fit: cover; display: block; }
.bs-img-placeholder {
  width: 100%; height: 180px;
  display: flex; align-items: center; justify-content: center;
  font-size: 48px;
}

.bs-hotel-name {
  font-size: 15px; font-weight: 800; color: #1a1a2e;
  padding: 14px 16px 2px;
  line-height: 1.3;
}
.bs-hotel-sub {
  font-size: 12px; color: #888;
  padding: 0 16px 12px;
}

.bs-divider { height: 1px; background: #f0f0f0; margin: 0 16px; }

/* 체크인/아웃 */
.bs-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
}
.bs-col { flex: 1; }
.bs-col-sep { color: #ccc; font-size: 16px; flex-shrink: 0; }
.bs-label {
  font-size: 10px; font-weight: 700;
  color: #999; text-transform: uppercase;
  letter-spacing: 0.5px; margin-bottom: 3px;
}
.bs-val { font-size: 14px; font-weight: 700; color: #1a1a2e; }

/* 태그 */
.bs-tags {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 0 16px 12px;
}
.bs-tag {
  font-size: 11px; font-weight: 600;
  background: #f0f4ff; color: #3b5bdb;
  padding: 3px 10px; border-radius: 20px;
}

/* 객실 정보 */
.bs-room-info  { padding: 12px 16px; }
.bs-room-name  { font-size: 13px; font-weight: 700; color: #1a1a2e; margin-top: 4px; line-height: 1.4; }
.bs-room-sub   { font-size: 11px; color: #888; margin-top: 2px; }

/* ✅ 식사 정보 태그 */
.bs-meal-tag {
  display: inline-block;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #c47f00;
  background: #fff8e6;
  border-radius: 20px;
  padding: 3px 10px;
}

/* 취소 정책 */
.bs-cancel     { padding: 12px 16px; }
.bs-cancel-val { font-size: 12px; color: #276749; font-weight: 600; margin-top: 4px; }

/* 가격 */
.bs-price-wrap  { padding: 14px 16px 16px; background: #f8f9ff; }
.bs-price-label { font-size: 11px; font-weight: 700; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.bs-price-val   { font-size: 26px; font-weight: 900; color: #1a1a2e; line-height: 1.1; }
.bs-price-val small { font-size: 13px; font-weight: 500; }
.bs-price-sub   { font-size: 11px; color: #999; margin-top: 3px; }

/* ✅ 모바일 — 요약 패널 상단으로 */
@media (max-width: 768px) {
  .bk-layout    { flex-direction: column-reverse; }
  .bk-summary   { width: 100%; position: static; }
  .booking-wrap { padding: 16px; }
  .gb-row {
    flex-direction: column;
  }
  .gb-input {
    width: 100%;
    box-sizing: border-box;
  }
  .gb-select {
    width: 100%;
    box-sizing: border-box;
  }
  .gb-select-nation {
    width: 100%;
  }
  .bk-section {
    /* width: auto; ← flex:1로 바뀌면서 이 줄은 필요 없어짐, 삭제 */
    padding: 20px 16px;
  }
}

/** 
  추가 안내 
*/
.bs-addinfo {
  padding: 14px 16px;
  background: #fffbeb;
  border-top: 1px solid #fde68a;
}
.bs-addinfo-title {
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
  margin-bottom: 10px;
  letter-spacing: 0.3px;
}
.bs-addinfo-item {
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #fde68a;
}
.bs-addinfo-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
.bs-addinfo-name {
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 4px;
}
.bs-addinfo-content {
  font-size: 12px;
  color: #78350f;
  line-height: 1.7;
  white-space: pre-line;   /* \n 줄바꿈 처리 */
}

.gb-select-nation {
  flex: 1;
  height: 38px;
}

.gb-select:disabled {
  background: #f5f5f5;
  color: #aaa;
  cursor: not-allowed;
}

/**
  Toss PG
*/
.pay-widget-wrap {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
}
</style>